import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import {
  MeetingSchema,
  type MeetingScope,
  meetingScopeKey,
  meetingScopeUrl,
} from '@/entities/meeting'
import { apiRequest } from '@/shared/api'

// Встреча из телемоста в ленте — узкий вид, а не полный снимок проекции:
// правится и удаляется она в источнике, поэтому наружу торчат только ссылки.
// Их бэкенд уже выбрал под смотрящего (заказчику — его контур, никогда
// guest_url): `url` — страница встречи, `join_url` — вход в комнату, и он
// приезжает, только пока звонок идёт. Фаза — словарь источника, enum'ом не
// нарроим.
export const TelemostMeetingSchema = z.object({
  id: z.string(),
  company_id: z.string().optional(),
  project_id: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  phase: z.string(),
  scheduled_at: z.string().optional(),
  started_at: z.string().optional(),
  ended_at: z.string().optional(),
  private: z.boolean(),
  customer_visible: z.boolean().optional().default(false),
  url: z.string().optional(),
  join_url: z.string().optional(),
  // Организатор приезжает именем, а не актором: у источника своя идентичность.
  organizer: z.string().optional(),
})
export type TelemostMeeting = z.infer<typeof TelemostMeetingSchema>

// Лента соединяет два домена — свою встречу и проекцию телемоста. Импорт
// entity→entity FSD запрещает, поэтому схема ленты и её запрос живут в виджете:
// он и есть точка композиции. Ключ запроса остаётся у `entities/meeting` — по
// нему инвалидируют фичи.
export const MeetingFeedItemSchema = z.object({
  kind: z.enum(['own', 'telemost']),
  when: z.string(),
  own: MeetingSchema.optional(),
  telemost: TelemostMeetingSchema.optional(),
})
export type MeetingFeedItem = z.infer<typeof MeetingFeedItemSchema>

export function meetingFeedQueryOptions(scope: MeetingScope) {
  return queryOptions({
    queryKey: meetingScopeKey(scope),
    queryFn: async () => {
      // items у huma nullable — пустая лента приезжает и как [], и как null.
      const body = await apiRequest<{ items: unknown[] | null }>({
        method: 'GET',
        url: meetingScopeUrl(scope),
      })
      return z.array(MeetingFeedItemSchema).parse(body.items ?? [])
    },
  })
}
